import React from 'react';
import PropTypes from 'prop-types';
import {
  Flex,
  FlexRow,
  Col,
  Body,
  Box,
  Heading,
} from '@edreamsodigeo/prisma-design-system';
import { useFlowTools } from 'flowTools/useFlowTools';
import {
  MEMBER_PRICE_POLICY_DISCOUNTED,
  MEMBER_PRICE_POLICY_UNDISCOUNTED,
  MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON,
} from '../../../../Shared/FeeSelector/FeeTypeName/feeTypeConstantNames';
import { useTranslation } from '@edreamsodigeo/frontend-core';
import DealPrice from '../../Deals/DealPrice';
import { useItinerary } from '@components/Results/Components/Itinerary/Provider';
import { MoneyComponent, RawMoneyComponent } from '../../../../Shared/Money';
import DiscountPillVersioned from '@components/Results/Components/DiscountPill/DiscountPIllVersioned';

export const getPrices = (fees = []) =>
  fees.reduce((prices, { price, type }) => {
    const feeId = type?.id || type;
    if (feeId === MEMBER_PRICE_POLICY_DISCOUNTED) {
      prices.primePrice = price;
    } else if (feeId === MEMBER_PRICE_POLICY_UNDISCOUNTED) {
      prices.noPrimePrice = price;
    } else if (feeId === MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON) {
      prices.primeExtraDiscountPrice = price;
    }

    return prices;
  }, {});

const MoneyWrapper = ({ price, sx }) => {
  const { dealInfo } = useItinerary();

  return dealInfo ? (
    <DealPrice price={price} />
  ) : (
    <MoneyComponent
      size="medium"
      currency={price.currency}
      strikeThrough="none"
      sx={sx}
    >
      {price.amount}
    </MoneyComponent>
  );
};

const PrimePrice = ({ price, sx }) => {
  return (
    <Box className="of_autos-itinerary-prime-price">
      <MoneyWrapper price={price} sx={sx} />
    </Box>
  );
};

const NoPrimePrice = ({ price, strikeThrough = 'default', isLongCurrency }) => {
  return !isLongCurrency ? (
    <Box sx={{ marginBottom: '-4px' }}>
      <Heading color="neutral.80" type="5" weight="normal">
        <RawMoneyComponent
          currency={price.currency}
          strikeThrough={strikeThrough}
        >
          {price.amount}
        </RawMoneyComponent>
      </Heading>
    </Box>
  ) : (
    <LongCurrencyNoPrimePrice price={price} />
  );
};

const LongCurrencyNoPrimePrice = ({ price }) => {
  return (
    <Box>
      <MoneyComponent
        color={'neutral.70'}
        currency={price.currency}
        sx={{
          '.money-integer': {
            color: 'neutral.70',
            fontSize: '14px',
            fontWeight: '400',
            textDecoration: 'line-through',
            textDecorationColor: '##909090',
          },
          '.money-currency': {
            display: 'block',
            fontSize: '14px',
            color: 'neutral.70',
            textAlign: 'right',
            fontWeight: '400',
            marginBottom: '-6px',
            marginRight: '-3px',
            textDecoration: 'line-through',
            textDecorationColor: '##909090',
          },
        }}
      >
        {price.amount}
      </MoneyComponent>
    </Box>
  );
};

const VersionedRow = ({ children, ...rest }) => (
  <FlexRow gap="2x_small" {...rest}>
    {children}
  </FlexRow>
);

const PriceLabel = ({ fontStyle, trialSidebar }) => {
  const { t } = useTranslation();

  return (
    <Body
      color={'neutral.70'}
      type="xSmall"
      fontStyle={fontStyle}
      textAlign={'left'}
      sx={{ whiteSpace: trialSidebar ? 'normal' : 'pre' }}
    >
      {t('prime.price.label')}
    </Body>
  );
};

const ResultsItineraryDualPrice = ({
  fees,
  align,
  fontStyle = 'normal',
  strikeThrough = 'default',
  trialSidebar,
  moneySx,
  isLongCurrency,
}) => {
  const { isDesktop, isMobile } = useFlowTools();
  const { primePrice, noPrimePrice } = getPrices(fees);
  const alignItems = align === 'left' ? 'flex-start' : 'flex-end';
  const { dealInfo, campaignConfig } = useItinerary();
  const isPrimeDayFare = campaignConfig?.primeDayConfig?.isPrimeDayFare;
  if (!primePrice && !noPrimePrice) return null;
  return (
    <Flex flexDirection="column" alignItems={alignItems}>
      <Flex flexDirection="column" alignItems={'flex-end'}>
        <Box>
          <DiscountPillVersioned
            price={noPrimePrice.amount}
            discountedPrice={primePrice.amount}
            isPrimeDayFare={isPrimeDayFare}
          />
        </Box>
        {!isLongCurrency ? (
          <VersionedRow
            alignItems="baseline"
            flexWrap="wrap"
            justifyContent={alignItems}
            sx={{ marginTop: isMobile ? '-16px' : '' }}
          >
            <Col>
              <NoPrimePrice
                price={noPrimePrice}
                strikeThrough={
                  dealInfo && isDesktop ? 'negative' : strikeThrough
                }
              />
            </Col>
            <Col>
              <PrimePrice price={primePrice} sx={moneySx} />
            </Col>
          </VersionedRow>
        ) : (
          <>
            <VersionedRow>
              <Col>
                <NoPrimePrice
                  price={noPrimePrice}
                  strikeThrough={
                    dealInfo && isDesktop ? 'negative' : strikeThrough
                  }
                  isLongCurrency={isLongCurrency}
                />
              </Col>
            </VersionedRow>
            <VersionedRow
              alignItems="baseline"
              flexWrap="wrap"
              justifyContent={alignItems}
              sx={{ marginTop: isMobile ? '-16px' : '' }}
            >
              <Col>
                <PrimePrice price={primePrice} sx={moneySx} />
              </Col>
            </VersionedRow>
          </>
        )}
      </Flex>
      <Flex width={trialSidebar ? '100%' : 'auto'} alignItems={'baseline'}>
        <PriceLabel fontStyle={fontStyle} trialSidebar={trialSidebar} />
      </Flex>
    </Flex>
  );
};

ResultsItineraryDualPrice.defaultProps = {
  align: 'right',
  trialSidebar: false,
  isPrimeExtraDiscountApplied: false,
};

ResultsItineraryDualPrice.propTypes = {
  fees: PropTypes.array.isRequired,
  align: PropTypes.oneOf(['right', 'left']),
  trialSidebar: PropTypes.bool,
  isPrimeExtraDiscountApplied: PropTypes.bool,
  moneySx: PropTypes.object,
  isLongCurrency: PropTypes.bool,
};

export default ResultsItineraryDualPrice;
